import React from 'react';

interface PaginationState {
  pageSize: number;
  skip: number;
  searchTerm: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface ChatHistoryPaginationProps {
  currentPage: number;
  totalPages: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const ChatHistoryPagination: React.FC<ChatHistoryPaginationProps> = ({
  currentPage,
  totalPages,
  pagination,
  setPagination,
  setCurrentPage,
}) => {
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setPagination((prev) => ({
      ...prev,
      skip: (newPage - 1) * prev.pageSize,
    }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      skip: (currentPage - 1) * newPageSize,
    }));
    setCurrentPage(1); // Reset to page 1 when page size changes
  };

  const renderPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => {
      const pageNumber = i + 1;
      
      // Show first page, last page, and pages around current page
      if (
        pageNumber === 1 || 
        pageNumber === totalPages || 
        (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
      ) {
        return (
          <button
            key={pageNumber}
            onClick={() => handlePageChange(pageNumber)}
            className={`px-3 py-1 border rounded-md ${
              currentPage === pageNumber 
                ? 'bg-gray-900 text-white' 
                : 'hover:bg-gray-50'
            }`}
          >
            {pageNumber}
          </button>
        );
      } else if (
        pageNumber === currentPage - 3 || 
        pageNumber === currentPage + 3
      ) {
        return <span key={pageNumber}>...</span>;
      }
      return null;
    });
  };

  return (
    <div className="flex justify-center items-center gap-2 mt-6 mb-4">
      {/* Previous Button */}
      <button
        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
        className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
      >
        Previous
      </button>

      {/* Page Numbers */}
      {renderPageNumbers()}

      {/* Next Button */}
      <button
        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
      >
        Next
      </button>

      {/* Page Size Selector */}
      <div className="flex items-center gap-2 ml-4">
        <label htmlFor="pageSize" className="text-sm font-medium text-gray-900">
          Page Size:
        </label>
        <select
          id="pageSize"
          value={pagination.pageSize}
          onChange={handlePageSizeChange}
          className="px-2 py-1 border border-gray-300 rounded-md"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={50}>50</option>
        </select>
      </div>
    </div>
  );
};

export default ChatHistoryPagination;